import { Spinner } from '@mcgames/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CancelImportButton } from '../games/CancelImportButton.tsx';
import { ErrorText } from '../games/ErrorText.tsx';
import adminStyles from '../Admin.module.css';
import { IndexerSearch } from './IndexerSearch.tsx';
import { importReasonDetail, importStatusText } from './import-text.ts';
import {
  useImport,
  useIndexerStatus,
  useRunningImportIds,
  useStartImport,
} from './indexer-hooks.ts';
import styles from './Indexer.module.css';

export function IndexerPage() {
  const { t } = useTranslation('admin');
  const status = useIndexerStatus();
  const start = useStartImport();
  // The import of each release started on this page, by release guid.
  const [importIds, setImportIds] = useState<Record<string, string>>({});
  const runningIds = useRunningImportIds(Object.values(importIds));

  return (
    <div className={adminStyles['page']}>
      <h1 className={adminStyles['heading']}>{t('indexer.title')}</h1>
      <p className={adminStyles['muted']}>{t('indexer.intro')}</p>
      {status.isPending && <Spinner />}
      <ErrorText error={status.error} />
      {status.data?.enabled === false && (
        <p role="status" className={adminStyles['muted']}>
          {t('indexer.disabled')}
        </p>
      )}
      {status.data?.enabled === true && (
        <>
          <IndexerSearch
            actionLabel={t('indexer.import')}
            pendingGuid={start.isPending ? (start.variables?.guid ?? null) : null}
            isBusy={(release) => runningIds.has(importIds[release.guid] ?? '')}
            onPick={(release) => {
              start.mutate(
                { guid: release.guid, title: release.title },
                {
                  onSuccess: (info) =>
                    setImportIds((current) => ({ ...current, [release.guid]: info.id })),
                },
              );
            }}
            rowStatus={(release) => {
              const id = importIds[release.guid];
              return id === undefined ? null : <ImportStatusLine id={id} />;
            }}
          />
          <ErrorText error={start.error} />
        </>
      )}
    </div>
  );
}

function ImportStatusLine({ id }: { id: string }) {
  const { t } = useTranslation('admin');
  const info = useImport(id);

  if (info.isError) {
    return (
      <span role="alert" className={styles['importProblem']}>
        {t('indexer.status.loadFailed')}
      </span>
    );
  }
  if (info.data === undefined) return <Spinner />;

  const { status, gameId } = info.data;
  const detail = importReasonDetail(info.data);
  const tone =
    status === 'running'
      ? 'importRunning'
      : status === 'failed' || status === 'needs_review'
        ? 'importProblem'
        : 'importDone';
  return (
    <span className={styles['importStatus']}>
      <span role="status" className={styles[tone]}>
        {importStatusText(t, info.data)}
      </span>
      {gameId !== null && (
        <Link to={`/admin/games/${encodeURIComponent(gameId)}`} className={styles['gameLink']}>
          {t('indexer.openGame')}
        </Link>
      )}
      {status === 'running' && <CancelImportButton info={info.data} size="sm" />}
      {detail !== null && <span className={styles['importDetail']}>{detail}</span>}
    </span>
  );
}
